/**
 * API Lauria: recherche d'entités Keycloak à importer.
 * Responsabilité: exposer des hooks RTK Query dédiés à la modale d'import Keycloak
 * et normaliser les réponses backend vers un modèle UI unique.
 */

import { baseApi } from 'core/api/base'
import type {
  KeycloakCompteServiceApi,
  KeycloakImportItem,
  KeycloakUtilisateurApi,
} from 'lauria/types/keycloakImport'

const KEYCLOAK_UTILISATEURS_ENDPOINT = '/keycloak/utilisateurs'
const KEYCLOAK_COMPTES_SERVICE_ENDPOINT = '/keycloak/comptes-service'

function buildUtilisateurLabel(user: KeycloakUtilisateurApi): string {
  const firstName = user.firstName?.trim()
  const lastName = user.lastName?.trim()

  if (firstName && lastName) {
    return `${lastName} ${firstName}`
  }

  if (lastName) {
    return lastName
  }

  if (firstName) {
    return firstName
  }

  return user.username
}

function mapUtilisateurToImportItem(
  user: KeycloakUtilisateurApi
): KeycloakImportItem {
  return {
    id: user.id,
    type: 'UTILISATEUR',
    identifier: user.username,
    label: buildUtilisateurLabel(user),
    email: user.email,
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
  }
}

function mapCompteServiceToImportItem(
  serviceAccount: KeycloakCompteServiceApi
): KeycloakImportItem {
  return {
    id: serviceAccount.id,
    type: 'COMPTE_SERVICE',
    identifier: serviceAccount.username,
    label: serviceAccount.username,
  }
}

const keycloakImportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchKeycloakUtilisateurs: builder.query<
      KeycloakImportItem[],
      { search: string }
    >({
      query: ({ search }) => ({
        url: KEYCLOAK_UTILISATEURS_ENDPOINT,
        params: { search },
      }),
      transformResponse: (response: KeycloakUtilisateurApi[]) =>
        response.map(mapUtilisateurToImportItem),
    }),

    searchKeycloakComptesService: builder.query<
      KeycloakImportItem[],
      { search: string }
    >({
      query: ({ search }) => ({
        url: KEYCLOAK_COMPTES_SERVICE_ENDPOINT,
        params: { clientId: search },
      }),
      transformResponse: (response: KeycloakCompteServiceApi[]) =>
        response.map(mapCompteServiceToImportItem),
    }),
  }),
})

export const {
  useLazySearchKeycloakUtilisateursQuery,
  useLazySearchKeycloakComptesServiceQuery,
} = keycloakImportApi

export { keycloakImportApi }
